import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosLogistics } from '../axios/api';

// ─── Thunks ──────────────────────────────────────────────────────────────────

export const fetchAirVendors = createAsyncThunk(
  'airVendors/fetchAirVendors',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') params.append(key, value);
      });
      const response = await axiosLogistics.get(`/logistics/air-vendors?${params}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch air vendors');
    }
  }
);

export const fetchAirVendorById = createAsyncThunk(
  'airVendors/fetchAirVendorById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosLogistics.get(`/logistics/air-vendors/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch air vendor');
    }
  }
);

export const createAirVendor = createAsyncThunk(
  'airVendors/createAirVendor',
  async (vendorData, { rejectWithValue }) => {
    try {
      const response = await axiosLogistics.post('/logistics/air-vendors', vendorData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to create air vendor' });
    }
  }
);

export const updateAirVendor = createAsyncThunk(
  'airVendors/updateAirVendor',
  async ({ id, vendorData }, { rejectWithValue }) => {
    try {
      const response = await axiosLogistics.put(`/logistics/air-vendors/${id}`, vendorData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update air vendor');
    }
  }
);

export const deleteAirVendor = createAsyncThunk(
  'airVendors/deleteAirVendor',
  async (id, { rejectWithValue }) => {
    try {
      await axiosLogistics.delete(`/logistics/air-vendors/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete air vendor');
    }
  }
);

// ─── Contact Thunks ──────────────────────────────────────────────────────────

export const addAirVendorContact = createAsyncThunk(
  'airVendors/addContact',
  async ({ vendorId, name, phone, email, position }, { rejectWithValue }) => {
    try {
      const res = await axiosLogistics.post(`/logistics/air-vendors/${vendorId}/contacts`, { name, phone, email, position });
      return { vendorId, contact: res.data.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add contact');
    }
  }
);

export const updateAirVendorContact = createAsyncThunk(
  'airVendors/updateContact',
  async ({ vendorId, contactId, name, phone, email, position }, { rejectWithValue }) => {
    try {
      const res = await axiosLogistics.put(`/logistics/air-vendors/${vendorId}/contacts/${contactId}`, { name, phone, email, position });
      return { vendorId, contact: res.data.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update contact');
    }
  }
);

export const removeAirVendorContact = createAsyncThunk(
  'airVendors/removeContact',
  async ({ vendorId, contactId }, { rejectWithValue }) => {
    try {
      await axiosLogistics.delete(`/logistics/air-vendors/${vendorId}/contacts/${contactId}`);
      return { vendorId, contactId };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to remove contact');
    }
  }
);

// ─── Contact Detail Thunks (multiple phone/email pairs per contact) ──────────

export const addAirVendorContactDetail = createAsyncThunk(
  'airVendors/addContactDetail',
  async ({ vendorId, contactId, phone, email }, { rejectWithValue }) => {
    try {
      const res = await axiosLogistics.post(`/logistics/air-vendors/${vendorId}/contacts/${contactId}/details`, { phone, email });
      return { vendorId, contactId, detail: res.data.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add contact detail');
    }
  }
);

export const updateAirVendorContactDetail = createAsyncThunk(
  'airVendors/updateContactDetail',
  async ({ vendorId, contactId, detailId, phone, email }, { rejectWithValue }) => {
    try {
      const res = await axiosLogistics.put(`/logistics/air-vendors/${vendorId}/contacts/${contactId}/details/${detailId}`, { phone, email });
      return { vendorId, contactId, detail: res.data.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update contact detail');
    }
  }
);

export const removeAirVendorContactDetail = createAsyncThunk(
  'airVendors/removeContactDetail',
  async ({ vendorId, contactId, detailId }, { rejectWithValue }) => {
    try {
      await axiosLogistics.delete(`/logistics/air-vendors/${vendorId}/contacts/${contactId}/details/${detailId}`);
      return { vendorId, contactId, detailId };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to remove contact detail');
    }
  }
);

// ─── Initial State ────────────────────────────────────────────────────────────

const initialState = {
  vendors: [],
  selectedVendor: null,
  pagination: { currentPage: 1, totalPages: 1, totalCount: 0, limit: 50 },
  filters: { page: 1, limit: 50, search: '', vendor_status: '', vendor_type: '', country: '' },
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  error: null,
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const airVendorSlice = createSlice({
  name: 'airVendors',
  initialState,
  reducers: {
    selectAirVendor:      (state, action) => { state.selectedVendor = action.payload; },
    clearSelectedAirVendor: (state) => { state.selectedVendor = null; },
    updateAirFilters:     (state, action) => { state.filters = { ...state.filters, ...action.payload }; },
    resetAirFilters:      (state) => { state.filters = initialState.filters; },
    clearAirError:        (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAirVendors.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAirVendors.fulfilled, (state, { payload }) => { state.loading = false; state.vendors = payload.vendors; state.pagination = payload.pagination; })
      .addCase(fetchAirVendors.rejected,  (state, { payload }) => { state.loading = false; state.error = payload; })

      .addCase(fetchAirVendorById.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAirVendorById.fulfilled, (state, { payload }) => { state.loading = false; state.selectedVendor = payload; })
      .addCase(fetchAirVendorById.rejected,  (state, { payload }) => { state.loading = false; state.error = payload; })

      .addCase(createAirVendor.pending,   (state) => { state.creating = true; state.error = null; })
      .addCase(createAirVendor.fulfilled, (state, { payload }) => { state.creating = false; state.vendors.unshift(payload); state.pagination.totalCount += 1; })
      .addCase(createAirVendor.rejected,  (state, { payload }) => { state.creating = false; state.error = payload; })

      .addCase(updateAirVendor.pending,   (state) => { state.updating = true; state.error = null; })
      .addCase(updateAirVendor.fulfilled, (state, { payload }) => {
        state.updating = false;
        const i = state.vendors.findIndex(v => v.id === payload.id);
        if (i !== -1) state.vendors[i] = payload;
        if (state.selectedVendor?.id === payload.id) state.selectedVendor = { ...state.selectedVendor, ...payload };
      })
      .addCase(updateAirVendor.rejected,  (state, { payload }) => { state.updating = false; state.error = payload; })

      .addCase(deleteAirVendor.pending,   (state) => { state.deleting = true; state.error = null; })
      .addCase(deleteAirVendor.fulfilled, (state, { payload }) => {
        state.deleting = false;
        state.vendors = state.vendors.filter(v => v.id !== payload);
        state.pagination.totalCount -= 1;
        if (state.selectedVendor?.id === payload) state.selectedVendor = null;
      })
      .addCase(deleteAirVendor.rejected,  (state, { payload }) => { state.deleting = false; state.error = payload; })

      // Contacts
      .addCase(addAirVendorContact.fulfilled, (state, { payload }) => {
        if (state.selectedVendor?.id === payload.vendorId) {
          if (!state.selectedVendor.contacts) state.selectedVendor.contacts = [];
          state.selectedVendor.contacts.push(payload.contact);
        }
      })
      .addCase(updateAirVendorContact.fulfilled, (state, { payload }) => {
        if (state.selectedVendor?.id === payload.vendorId) {
          const i = (state.selectedVendor.contacts || []).findIndex(c => c.id === payload.contact.id);
          if (i !== -1) state.selectedVendor.contacts[i] = payload.contact;
        }
      })
      .addCase(removeAirVendorContact.fulfilled, (state, { payload }) => {
        if (state.selectedVendor?.id === payload.vendorId) {
          state.selectedVendor.contacts = (state.selectedVendor.contacts || []).filter(c => c.id !== payload.contactId);
        }
      })

      // Contact details
      .addCase(addAirVendorContactDetail.fulfilled, (state, { payload }) => {
        if (state.selectedVendor?.id === payload.vendorId) {
          const c = (state.selectedVendor.contacts || []).find(c => c.id === payload.contactId);
          if (c) { if (!c.details) c.details = []; c.details.push(payload.detail); }
        }
      })
      .addCase(updateAirVendorContactDetail.fulfilled, (state, { payload }) => {
        if (state.selectedVendor?.id === payload.vendorId) {
          const c = (state.selectedVendor.contacts || []).find(c => c.id === payload.contactId);
          if (c) {
            const i = (c.details || []).findIndex(d => d.id === payload.detail.id);
            if (i !== -1) c.details[i] = payload.detail;
          }
        }
      })
      .addCase(removeAirVendorContactDetail.fulfilled, (state, { payload }) => {
        if (state.selectedVendor?.id === payload.vendorId) {
          const c = (state.selectedVendor.contacts || []).find(c => c.id === payload.contactId);
          if (c) c.details = (c.details || []).filter(d => d.id !== payload.detailId);
        }
      });
  },
});

export const { selectAirVendor, clearSelectedAirVendor, updateAirFilters, resetAirFilters, clearAirError } = airVendorSlice.actions;

export const selectAirVendors          = (state) => state.airVendors.vendors;
export const selectSelectedAirVendor   = (state) => state.airVendors.selectedVendor;
export const selectAirVendorContacts   = (state) => state.airVendors.selectedVendor?.contacts || [];
export const selectAirPagination       = (state) => state.airVendors.pagination;
export const selectAirFilters          = (state) => state.airVendors.filters;
export const selectAirLoading          = (state) => state.airVendors.loading;
export const selectAirCreating         = (state) => state.airVendors.creating;
export const selectAirUpdating         = (state) => state.airVendors.updating;
export const selectAirDeleting         = (state) => state.airVendors.deleting;
export const selectAirError            = (state) => state.airVendors.error;

export default airVendorSlice.reducer;
